/** @jsxImportSource @emotion/react */
import { SearchOutlined } from "@ant-design/icons";
import { css } from "@emotion/react";
import {
  Button,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Radio,
  Select,
  Space,
  Table,
} from "antd";
import _ from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import { useFormatMessage } from "src/hooks";
import messageIds from "src/locales/messageIds";
import { ServiceManage, UserServiceList } from "src/models/aiPlatform";
import { AiServiceClient } from "src/services/AIPlatformService";
import "./ServiceOperation.css";

const { Option } = Select;

export default React.memo(
  (props: {
    isCreateShow?: boolean;
    onCreate?: () => void;
    fetch: () => void;
  }) => {
    const { isCreateShow, onCreate, fetch } = props;
    const formatMessage = useFormatMessage();
    const [form] = Form.useForm();
    const [dataSource, setDataSource] = useState([] as ServiceManage[]);
    // const [userId, setUserId] = useState(null);
    const [type, setType] = useState(0);
    // const [maxCallLimited, setMaxCallLimited] = useState<number | undefined>(
    //   undefined
    // );
    const [data, setData] = useState([] as UserServiceList[]);
    const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
    const [selectData, setSelectData] = useState(
      [] as { id: string; name: string; email: string; phone: string }[]
    );
    const [recordRow, setRecordRow] = useState({} as ServiceManage);
    const [unSelectedData, setUnSelectedData] = useState(
      [] as UserServiceList[]
    );
    const [unSelectedId, setUnSelectedId] = useState<number[]>([]);
    const [idandlimit, setIdandlimit] = useState([] as UserServiceList[]);
    const [idandtype, setIdandtype] = useState([] as UserServiceList[]);
    const [loading, setLoading] = useState(true);
    // const [searchText, setSearchText] = useState("");
    // const [searchedColumn, setSearchedColumn] = useState("");

    const editFetch = useCallback(() => {
      AiServiceClient.getServiceManage().then((res) => {
        setDataSource(res.data.data);
        const thdata = [];
        const madata = [];
        for (let i = 0; i < res.data.data.length; i++) {
          thdata.push({
            serviceId: res.data.data[i].serviceId,
            type: 0,
          });
          setIdandtype([...idandtype, ...thdata]);

          madata.push({
            serviceId: res.data.data[i].serviceId,
            maxCallLimited: res.data.data[i].platformServiceInfo.trialLimited,
          });
          setIdandlimit([...idandlimit, ...madata]);
        }
        setLoading(false);
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (isCreateShow) {
        editFetch();
      }
    }, [editFetch, isCreateShow]);

    const onCancel = useCallback(() => {
      if (isCreateShow) {
        onCreate && onCreate();
        form.resetFields();
        // setMaxCallLimited(undefined);
        setData([]);
        setType(0);
        setSelectedRowKeys([]);
        setIdandlimit([]);
        setIdandtype([]);
        setUnSelectedData([]);
        setSelectData([]);
        setRecordRow({} as ServiceManage);
      }
    }, [onCreate, isCreateShow, form, setData]);

    const nameSearch = useCallback((selectedKeys, confirm, dataIndex) => {
      confirm();
      // setSearchText(selectedKeys[0]);
      // setSearchedColumn(dataIndex);
    }, []);

    const handleReset = useCallback((clearFilters) => {
      clearFilters();
      // setSearchText("");
    }, []);

    const columnsCreate = [
      {
        title: formatMessage(messageIds.aiManage.serviceManagement.name),
        dataIndex: "name",
        sorter: (a: ServiceManage, b: ServiceManage) =>
          a.platformServiceInfo.name?.localeCompare(b.platformServiceInfo.name),
        filterDropdown: ({
          setSelectedKeys,
          selectedKeys,
          confirm,
          clearFilters,
        }: any) => (
          <div
            css={css`
              padding: 8px;
            `}
          >
            <Input
              placeholder={formatMessage(messageIds.common.searchName)}
              value={selectedKeys[0]}
              onChange={(e) =>
                setSelectedKeys(e.target.value ? [e.target.value] : [])
              }
              onPressEnter={() => nameSearch(selectedKeys, confirm, "name")}
              css={css`
                margin-bottom: 8px;
                display: block;
              `}
            ></Input>
            <Space>
              <Button
                type="primary"
                onClick={() => nameSearch(selectedKeys, confirm, "name")}
                icon={<SearchOutlined />}
                size="small"
                css={css`
                  width: 90px;
                `}
              >
                {formatMessage(messageIds.common.select)}
              </Button>
              <Button
                onClick={() => handleReset(clearFilters)}
                size="small"
                css={css`
                  width: 90px;
                `}
              >
                {formatMessage(messageIds.common.reset)}
              </Button>
            </Space>
          </div>
        ),
        filterIcon: (filtered: any) => (
          <SearchOutlined
            css={css`
              color: ${filtered ? "#1890ff" : undefined};
            `}
          ></SearchOutlined>
        ),
        onFilter: (value: any, record: any) =>
          record?.platformServiceInfo?.name &&
          record?.platformServiceInfo?.name.includes(_.trim(value)),
        onFilterDropdownVisibleChange: (visible: boolean) => {
          if (visible) {
            setTimeout(() => nameSearch, 100);
          }
        },
        render: (text: string, record: ServiceManage) => (
          <span>{record?.platformServiceInfo?.name}</span>
        ),
      },
      {
        title: formatMessage(
          messageIds.aiManage.userServiceManagement.createService.type
        ),
        dataIndex: "type",
        width: 196,
        render: (text: number, record: ServiceManage) => (
          <Radio.Group
            defaultValue={type}
            onChange={(e) => {
              setType(e.target.value);
              const data = [];
              data.push({
                serviceId: record.serviceId,
                type: e.target.value,
              });
              if (idandtype.length > 0) {
                const now = idandtype.filter(
                  (item) => item.serviceId !== record.serviceId
                );
                setIdandtype([...now, ...data]);
              } else {
                setIdandtype(data);
              }
            }}
          >
            <Radio value={0}>
              {formatMessage(
                messageIds.aiManage.userServiceManagement.createService.trial
              )}
            </Radio>
            <Radio value={1}>
              {formatMessage(
                messageIds.aiManage.userServiceManagement.createService.formal
              )}
            </Radio>
          </Radio.Group>
        ),
      },
      {
        title: formatMessage(
          messageIds.aiManage.userServiceManagement.createService.maxCallLimited
        ),
        dataIndex: "maxCallLimited",
        render: (text: number, record: ServiceManage) => (
          <InputNumber
            css={css`
              width: 100px;
            `}
            defaultValue={record.platformServiceInfo.trialLimited}
            onChange={(e) => {
              // setMaxCallLimited(e);
              const data = [];
              data.push({
                serviceId: record.serviceId,
                maxCallLimited: e,
              });
              if (idandlimit.length > 0) {
                const now = idandlimit.filter(
                  (item) => item.serviceId !== record.serviceId
                );
                setIdandlimit([...now, ...data]);
              } else {
                setIdandlimit(data);
              }
            }}
          ></InputNumber>
        ),
      },
    ];

    const selectFetch = useCallback((value) => {
      let timeout: NodeJS.Timeout | null = null;

      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
      const fake = () =>
        AiServiceClient.getUserListBySearch(_.trim(value)).then((res) => {
          const data: {
            id: string;
            name: string;
            email: string;
            phone: string;
          }[] = [];
          // eslint-disable-next-line array-callback-return
          res.data.data.map((item: any) => {
            data.push({
              id: item.userId,
              name: item.userInfo.name,
              email: item.userInfo.email,
              phone: item.userInfo.phone,
            });
          });
          setSelectData(data);
        });
      timeout = setTimeout(fake, 100);
    }, []);

    const handleSearch = useCallback(
      (e) => {
        if (e) {
          selectFetch(e);
        } else {
          setSelectData([]);
        }
      },
      [selectFetch, setSelectData]
    );

    // const handleChange = useCallback(
    //   (e) => {
    //     setUserId(e);
    //   },
    //   [setUserId]
    // );

    const onRowClick = useCallback(
      (record, index) => {
        setRecordRow(record);
      },
      [setRecordRow]
    );

    const onSelectChange = useCallback(
      (selectedRowKeys, selectedRow) => {
        setSelectedRowKeys(selectedRowKeys);
      },
      [setSelectedRowKeys]
    );

    useEffect(() => {
      const selectedData: UserServiceList[] = [];
      if (selectedRowKeys.length > 0) {
        if (!recordRow.serviceId) {
          // eslint-disable-next-line array-callback-return
          selectedRowKeys.map((item) => {
            selectedData.push({
              serviceId: item,
              type: idandtype.filter((type) => type.serviceId === item)[0]
                ?.type,
              maxCallLimited: idandlimit.filter(
                (limit) => limit.serviceId === item
              )[0]?.maxCallLimited,
            });
          });
          setData(selectedData);
        } else {
          const firth = selectedRowKeys.filter(
            (item) => item === recordRow.serviceId
          );
          if (firth.length > 0) {
            selectedData.push({
              serviceId: recordRow.serviceId,
              type: idandtype.filter(
                (type) => type.serviceId === recordRow.serviceId
              )[0]?.type,
              maxCallLimited: idandlimit.filter(
                (limit) => limit.serviceId === recordRow.serviceId
              )[0]?.maxCallLimited,
            });
            if (data.length > 0) {
              const knownId = [];
              const unknownData = [];
              for (let i = 0; i < selectedRowKeys.length; i++) {
                knownId.push(
                  data.filter(
                    (item) => item.serviceId === selectedRowKeys[i]
                  )[0]?.serviceId
                );

                if (knownId.indexOf(selectedRowKeys[i]) === -1) {
                  unknownData.push({
                    serviceId: selectedRowKeys[i],
                    type: idandtype.filter(
                      (type) => type.serviceId === selectedRowKeys[i]
                    )[0]?.type,
                    maxCallLimited: idandlimit.filter(
                      (limit) => limit.serviceId === selectedRowKeys[i]
                    )[0]?.maxCallLimited,
                  });
                  setData([...data, ...unknownData]);
                } else {
                  const nowData = data.filter(
                    (item) => item.serviceId !== recordRow.serviceId
                  );
                  setData([...nowData, ...selectedData]);
                }
              }
            } else {
              const nowData = [];
              for (let i = 0; i < selectedRowKeys.length; i++) {
                nowData.push({
                  serviceId: selectedRowKeys[i],
                  type: idandtype.filter(
                    (type) => type.serviceId === selectedRowKeys[i]
                  )[0]?.type,
                  maxCallLimited: idandlimit.filter(
                    (limit) => limit.serviceId === selectedRowKeys[i]
                  )[0]?.maxCallLimited,
                });
              }
              for (let i = 0; i < unSelectedId.length; i++) {
                // eslint-disable-next-line array-callback-return
                nowData.map((item) => {
                  if (item.serviceId === unSelectedId[i]) {
                    const nowUnSelectedId = unSelectedId.filter(
                      (serviceId) => serviceId !== item.serviceId
                    );
                    const nowUnSelectedData = unSelectedData.filter(
                      (data) => data.serviceId !== item.serviceId
                    );
                    setUnSelectedId(nowUnSelectedId);
                    setUnSelectedData(nowUnSelectedData);
                  }
                });
              }
              setData(nowData);
            }
          } else {
            const nowData: UserServiceList[] = [];
            for (let i = 0; i < selectedRowKeys.length; i++) {
              nowData.push({
                serviceId: selectedRowKeys[i],
                type: idandtype.filter(
                  (type) => type.serviceId === selectedRowKeys[i]
                )[0]?.type,
                maxCallLimited: idandlimit.filter(
                  (limit) => limit.serviceId === selectedRowKeys[i]
                )[0]?.maxCallLimited,
              });
            }
            setData(nowData);
          }
        }
      } else {
        const unselectedId = [];
        const unselectedData = [];
        const nowUnSelectedId = unSelectedId?.filter(
          (item) => item !== recordRow.serviceId && item !== undefined
        );
        const nowUnSelectedData = unSelectedData?.filter(
          (item) =>
            item.serviceId !== recordRow.serviceId &&
            item.serviceId !== undefined
        );

        unselectedId.push(recordRow.serviceId);
        unselectedData.push({
          serviceId: recordRow.serviceId,
          type: idandtype.filter(
            (item) => item.serviceId === recordRow.serviceId
          )[0]?.type,
          maxCallLimited: idandlimit.filter(
            (item) => item.serviceId === recordRow.serviceId
          )[0]?.maxCallLimited,
        });
        setUnSelectedId([...nowUnSelectedId, ...unselectedId]);
        setUnSelectedData([...nowUnSelectedData, ...unselectedData]);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idandtype, idandlimit, selectedRowKeys, recordRow]);

    const rowSelection = {
      selectedRowKeys,
      onChange: onSelectChange,
    };

    const OnAdd = useCallback(async () => {
      const dataLength = data.filter(
        (item) =>
          item.maxCallLimited !== undefined &&
          selectedRowKeys.indexOf(item?.serviceId) !== -1
      ).length;
      const selectLength = selectedRowKeys.length;
      if (dataLength >= selectLength) {
        const createData = {
          userId: (await form.validateFields()).name,
          userServiceList: data.filter(
            (item) =>
              item.maxCallLimited !== undefined &&
              selectedRowKeys.indexOf(item?.serviceId) !== -1
          ),
        };
        const limitedScale =
          createData.userServiceList.filter(
            (item) =>
              Number(item?.maxCallLimited) < 0 ||
              Number(item?.maxCallLimited) > 9999999
          ).length > 0;
        if (
          createData.userId !== null &&
          createData.userServiceList.length > 0
        ) {
          if (limitedScale) {
            message.error(
              formatMessage(messageIds.aiManage.userServiceManagement.scale)
            );
          } else {
            AiServiceClient.saveData(createData).then((res) => {
              if (res.data.success) {
                fetch();
                message.success(
                  formatMessage(messageIds.common.createSuccessfully)
                );
              } else {
                message.error(
                  `${formatMessage(messageIds.common.createFailed)}, ${
                    res.data.message
                  }`
                );
              }
            });
            onCancel();
          }
        } else {
          message.error(
            formatMessage(
              messageIds.aiManage.userServiceManagement.editService.tiptop
            )
          );
        }
      } else {
        message.error(
          formatMessage(
            messageIds.aiManage.userServiceManagement.editService.tip
          )
        );
      }
    }, [data, selectedRowKeys, form, formatMessage, onCancel, fetch]);

    return (
      <div>
        <Modal
          title={formatMessage(messageIds.common.create)}
          visible={isCreateShow}
          footer={""}
          destroyOnClose
          onCancel={onCancel}
        >
          <Form form={form} layout="vertical">
            <Form.Item
              label={formatMessage(
                messageIds.aiManage.userServiceManagement.createService
                  .searchName
              )}
              name="name"
              rules={[
                {
                  required: true,
                  message: formatMessage(
                    messageIds.aiManage.userServiceManagement.common.inputName
                  ),
                },
              ]}
            >
              <Select
                css={css`
                  width: 100%;
                `}
                showSearch
                placeholder={formatMessage(
                  messageIds.aiManage.userServiceManagement.createService.tiptop
                )}
                defaultActiveFirstOption={false}
                showArrow={false}
                filterOption={false}
                onSearch={handleSearch}
                // onChange={handleChange}
                notFoundContent={null}
              >
                {selectData.map((item) => (
                  <Option key={item.id} value={item.id}>
                    {item.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
          <Table
            scroll={{ y: 500, x: "100%" }}
            rowKey={(record) => record?.serviceId}
            columns={columnsCreate}
            dataSource={dataSource}
            pagination={false}
            rowSelection={rowSelection}
            onRow={(record, index) => ({
              onClick: () => onRowClick(record, index),
            })}
            loading={loading}
          ></Table>
          <div
            css={css`
              text-align: right;
              margin-top: 40px;
            `}
          >
            <Button
              css={css`
                height: 32px;
                min-width: 88px;
                border-radius: 4px;
              `}
              onClick={onCancel}
            >
              {formatMessage(messageIds.common.cancel)}
            </Button>
            <Button
              type="primary"
              css={css`
                height: 32px;
                min-width: 88px;
                border-radius: 4px;
                margin-left: 20px;
              `}
              onClick={OnAdd}
            >
              {formatMessage(messageIds.common.create)}
            </Button>
          </div>
        </Modal>
      </div>
    );
  }
);
